/*
  CUSTOM ADDITION — added 13.10.2026 by OH (updated 14.10.2026)
  Purpose: Quick add button on product cards.
  1) Keeps the cart drawer closed after a quick add. Only applies to forms that send
     on_success = quick_add (snippets/product-card.liquid). The product page keeps
     opening the drawer as before.
  2) Right after the click (reverted if adding fails), the button shows a checkmark for the duration
     set in Theme settings -> Product card -> Quick add button. Then the checkmark
     rotates and turns into the plus halfway through the rotation. If the pointer is
     still on the button, the plus stays until it leaves (no label right away).
     Styling in assets/theme.css (.is-added, .is-returning, .is-cooldown, .is-snap).
  3) Left alignment: moves the button 8px to the right of other badges in the
     bottom left corner.
*/
(function () {
  var BUTTON_SELECTOR = '.product-card__quick-add-button[data-confirm-duration]';
  var ROTATION_MS = 200;      // .2s rotation in theme.css
  var ROTATION_HALF_MS = 100; // switch from checkmark to plus halfway
  var timers = new WeakMap();

  // 1) Keep the cart drawer closed for quick adds.
  // Runs in the capture phase on window, so before the drawer receives the event.
  window.addEventListener('cart:change', function (event) {
    var detail = event.detail;
    if (detail && detail.baseEvent === 'variant:add' && detail.onSuccessDo === 'quick_add') {
      detail.baseEvent = 'variant:add:quick-add';
    }
  }, true);

  // 2) Checkmark after a successful add
  function clearTimers(button) {
    (timers.get(button) || []).forEach(clearTimeout);
    timers.set(button, []);
  }

  function later(button, fn, ms) {
    timers.get(button).push(setTimeout(fn, ms));
  }

  function returnToPlus(button) {
    button.classList.add('is-returning');

    later(button, function () {
      button.classList.remove('is-added');
      if (button.matches(':hover')) {
        button.classList.add('is-cooldown');
      }
    }, ROTATION_HALF_MS);

    later(button, function () {
      // End the rotation without animating back (plus at 90° looks like plus at 0°)
      button.classList.add('is-snap');
      button.classList.remove('is-returning');
      void button.offsetWidth;
      button.classList.remove('is-snap');
    }, ROTATION_MS);
  }

  function showCheckmark(button) {
    var seconds = parseFloat(button.getAttribute('data-confirm-duration')) || 4;
    clearTimers(button);
    button.classList.remove('is-returning', 'is-cooldown', 'is-snap');
    button.classList.add('is-added');

    later(button, function () {
      returnToPlus(button);
    }, seconds * 1000);
  }

  function getQuickAddButton(event) {
    var form = event.target;
    if (!form || !form.querySelector) return null;
    return form.querySelector(BUTTON_SELECTOR);
  }

  // Show the checkmark instantly on click, without waiting for the cart request
  document.addEventListener('submit', function (event) {
    var button = getQuickAddButton(event);
    if (!button) return;
    button.dataset.quickAddSubmitted = 'true';
    showCheckmark(button);
  }, true);

  // Fallback: Prestige confirms the add, but no checkmark was shown on click
  document.addEventListener('variant:add', function (event) {
    var button = getQuickAddButton(event);
    if (!button) return;
    if (button.dataset.quickAddSubmitted !== 'true') {
      showCheckmark(button);
    }
    delete button.dataset.quickAddSubmitted;
  });

  // Adding failed (e.g. out of stock): back to the plus right away
  document.addEventListener('cart:error', function (event) {
    var button = getQuickAddButton(event);
    if (!button) return;
    delete button.dataset.quickAddSubmitted;
    clearTimers(button);
    button.classList.remove('is-added', 'is-returning', 'is-snap');
  });

  // Pointer leaves the button: end the cooldown, next hover shows the label again
  document.addEventListener('mouseout', function (event) {
    var button = event.target.closest && event.target.closest(BUTTON_SELECTOR);
    if (!button || !button.classList.contains('is-cooldown')) return;
    if (event.relatedTarget && button.contains(event.relatedTarget)) return; // still inside
    button.classList.remove('is-cooldown');
  });

  // 3) Left alignment (Theme settings -> Quick add button -> Button alignment):
  // if other badges (e.g. Stoq preorder, sale) sit in the bottom left corner,
  // the button moves 8px to the right of them. Sets --quick-add-start-offset
  // on the button, used in assets/theme.css.
  var BADGE_GAP = 8; // px
  var BADGE_SELECTOR = '.product-card__badges-overlay .badge, .restock-rocket-preorder-badge';

  function rootVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  function positionButton(button) {
    button.style.removeProperty('--quick-add-start-offset');
    if (rootVar('--quick-add-alignment') !== 'left') return;

    var card = button.closest('.product-card');
    var container = button.offsetParent;
    if (!card || !container) return;

    var containerRect = container.getBoundingClientRect();
    var start = parseFloat(rootVar('--quick-add-edge-offset')) || 0;
    var width = button.offsetWidth;
    var top = button.offsetTop;
    var bottom = top + button.offsetHeight;

    // Visible badges on the same height as the button, sorted from left to right
    var badges = Array.prototype.map.call(card.querySelectorAll(BADGE_SELECTOR), function (badge) {
      var rect = badge.getBoundingClientRect();
      return {
        left: rect.left - containerRect.left,
        right: rect.right - containerRect.left,
        top: rect.top - containerRect.top,
        bottom: rect.bottom - containerRect.top
      };
    }).filter(function (b) {
      return b.right > b.left && b.bottom > top && b.top < bottom;
    }).sort(function (a, b) {
      return a.left - b.left;
    });

    var moved = false;
    badges.forEach(function (b) {
      if (b.left < start + width && b.right > start) {
        start = b.right + BADGE_GAP;
        moved = true;
      }
    });

    if (moved) {
      button.style.setProperty('--quick-add-start-offset', start + 'px');
    }
  }

  function positionAll() {
    document.querySelectorAll('.product-card__quick-add-button').forEach(positionButton);
  }

  // Check again whenever the pointer enters a card, so badges that apps like
  // Stoq add later are always taken into account before the button appears
  document.addEventListener('mouseover', function (event) {
    var card = event.target.closest && event.target.closest('.product-card');
    if (!card || card.contains(event.relatedTarget)) return;
    var button = card.querySelector('.product-card__quick-add-button');
    if (button) positionButton(button);
  });

  window.addEventListener('load', positionAll);
  window.addEventListener('resize', positionAll);
})();